import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import Profile from './components/Profile';
import UserBoard from './components/UserBoard';
import ExploreBoards from './components/ExploreBoards';
import { useAuth } from './contexts/AuthContext';
import { FloatingButton } from './components/FloatingButton';
import { AddPhotoModal } from './components/AddPhotoModal';
import styled from 'styled-components';
import { useToast } from './contexts/ToastContext';
import { visionBoardService } from './services/visionBoard.service';

// Ruta protegida que requiere autenticación
const PrivateRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Ruta pública que redirige si ya está autenticado
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/" />;
};

const App = () => {
  const { user, userProfile } = useAuth();
  const [isAddingPhoto, setIsAddingPhoto] = useState(false);
  const toast = useToast();
  const location = useLocation();

  // Verificar si el usuario está en su propio board
  const isUserBoard = () => {
    if (!user || !userProfile) return false;
    const pathParts = location.pathname.split('/');
    const boardUsername = pathParts[1];
    return pathParts.length === 3 && 
           pathParts[2] === 'board' && 
           boardUsername === userProfile.username;
  };

  const handleAddPhoto = async (newPhoto) => {
    try {
      if (!user?.uid) {
        throw new Error('Usuario no autenticado');
      }

      const photoData = {
        ...newPhoto,
        userId: user.uid,
        createdAt: new Date()
      };

      await visionBoardService.addCard(photoData);
      toast.showToast("Foto agregada correctamente", 'success');
      setIsAddingPhoto(false);

      if (userProfile?.username) {
        await visionBoardService.getUserBoard(user.uid, true);
      }
    } catch (error) {
      console.error('Error al agregar foto:', error);
      toast.showToast("Error al agregar la foto", 'error');
    }
  };

  return (
    <AppContainer>
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/explore" element={<ExploreBoards />} />
        
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route path="/:username/board" element={<UserBoard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {isUserBoard() && <FloatingButton onClick={() => setIsAddingPhoto(true)} />}
      {isAddingPhoto && isUserBoard() && (
        <AddPhotoModal
          addNewPhoto={handleAddPhoto}
          closeModal={() => setIsAddingPhoto(false)}
        />
      )}
    </AppContainer>
  );
};

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export default App;
